import { Layout } from 'antd'
import React, { useEffect, useState, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faMinus,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'
import { matchPath, useLocation, useHistory } from 'react-router'
import { useIsMobile } from 'hooks/useIsMobile'
import { AccessProvider } from '../../contexts/AccessContext'
import { IMainState } from '../../store/states'
import styles from './NewHome.module.scss'
import SidebarMenu from './components/sidebar/SidebarMenu'
import NavBar from './components/navbar/NavBar'
import { MobileMenu } from './components/mobile-menu/MobileMenu'
import { useMenuItems } from './components/useMenuItems'
import { SubMenu, SubMenuItem, SubMenuContainer, ScrollButton } from './styled'

const { Header, Sider, Content } = Layout

const NewHome: React.FC = ({ children }) => {
  const [siderIsCollapsed, setSiderIsCollapsed] = useState(false)
  const subMenuRef = useRef<HTMLDivElement>(null)
  const enterprise = useSelector(({ user }: IMainState) => user.user.enterprise)
  const { activeMenuItem, activeSubMenuItem, menuItems } = useMenuItems()
  const history = useHistory()
  const imgAltText = enterprise?.name
  const logoURL = enterprise?.logo

  const location = useLocation()

  useEffect(() => {
    if (
      matchPath(location.pathname, {
        path: [
          '/courses/:courseId',
          '/administration/courses/:courseId/preview',
        ],
      })
    ) {
      setSiderIsCollapsed(true)
    }
  }, [location])

  const isMobile = useIsMobile()

  const scrollLeft = () => {
    if (subMenuRef.current) {
      subMenuRef.current.scrollBy({ left: -200, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (subMenuRef.current) {
      subMenuRef.current.scrollBy({ left: 200, behavior: 'smooth' })
    }
  }

  return (
    <AccessProvider>
      <Layout>
        {!isMobile && (
          <Sider
            className={`sider ${
              siderIsCollapsed ? styles.siderCollapsed : styles.siderExtended
            } ${isMobile && !siderIsCollapsed ? styles.siderMobile : ''}`}
            collapsible={true}
            collapsedWidth={0}
            trigger={null}
          >
            <div>
              <div
                className={
                  styles.collapseSiderButton +
                  ' ' +
                  (siderIsCollapsed ? styles.collapsed : '')
                }
                onClick={() => setSiderIsCollapsed(!siderIsCollapsed)}
              >
                <FontAwesomeIcon
                  className={styles.collapseButtonIcon}
                  icon={siderIsCollapsed ? faPlus : faMinus}
                />
              </div>

              <div className={styles.companyContainer}>
                <div
                  className={
                    styles.rectangle +
                    ' ' +
                    (siderIsCollapsed ? styles.rectangleCollapsed : '')
                  }
                ></div>
                <div
                  className={
                    siderIsCollapsed
                      ? styles.logoCollapsed
                      : styles.logoContainer
                  }
                >
                  {!siderIsCollapsed && (
                    <img
                      onClick={() =>
                        window.open(
                          `${process.env.REACT_APP_APP_URL}/#/v2/home`,
                          '_self'
                        )
                      }
                      className={styles.logo}
                      src={logoURL}
                      alt={imgAltText}
                    />
                  )}
                </div>
              </div>
            </div>
            <SidebarMenu siderIsCollapsed={siderIsCollapsed} />
          </Sider>
        )}
        <Layout>
          <Header
            className={`${styles.header} ${isMobile ? styles.navMobile : ''}`}
          >
            <NavBar isMobile={isMobile} />
            {!!isMobile && <MobileMenu />}
          </Header>
          {menuItems[activeMenuItem] && menuItems[activeMenuItem].subItems && (
            <SubMenu>
              {isMobile && (
                <ScrollButton className="left" onClick={scrollLeft}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </ScrollButton>
              )}
              <SubMenuContainer ref={subMenuRef} isMobile={isMobile}>
                {menuItems[activeMenuItem].subItems?.map((item, index) => (
                  <SubMenuItem
                    key={index}
                    active={activeSubMenuItem === index}
                    onClick={() => history.push(item.url || '')}
                  >
                    {item.text}
                  </SubMenuItem>
                ))}
              </SubMenuContainer>
              {isMobile && (
                <ScrollButton className="right" onClick={scrollRight}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </ScrollButton>
              )}
            </SubMenu>
          )}
          <Content
            className={`${styles.content} ${
              isMobile ? styles.contentMobile : ''
            }`}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
    </AccessProvider>
  )
}

export default NewHome
